// Frozen contract for the Adaptive Sampling feature. Vocabulary is deliberately generic; no algorithm detail.

export type AxisSpec = {
  name: string;
  units: string;
  mode: 'range' | 'fixed';
  min?: number;
  max?: number;
  fixedValue?: number;
};

export type OutputSpec = {
  name: string;
  weight: number;
  targetSigma?: number;
};

export type SolverChoice = 'AVL' | 'JVL' | 'DATCOM' | 'panel' | 'Flow360';

export type OracleSpec = {
  enabled: boolean;
  datasetId?: string;
};

export type PostDivergenceSamplingPolicy = {
  enabled: boolean;
  axisName?: string;
  outputName?: string;
  minPoints?: number;
  dropFraction?: number;
  guardBand?: number;
};

export type SamplerEngine = 'mock-surrogate' | 'tailor-mannequin' | 'lhs-mannequin';

export type MannequinFamily = string;

export type SamplerSymmetryPolicy = {
  enabled: boolean;
  axisName: string;
  centre?: number;
  sampleSide?: 'positive' | 'negative';
  sampleHalf?: boolean;
  mirrorObservations?: boolean;
};

export type SamplerAxisPriority = {
  axisName: string;
  /** Coordinate that represents the preferred sweep (zero beta, for example). */
  target: number;
  /** Utility assigned to candidates close to the preferred coordinate. */
  targetWeight: number;
  /** Utility assigned to candidates towards either edge of this axis. */
  farWeight: number;
  /** Preferred-sweep half-width as a fraction of the full axis range. */
  targetWidth?: number;
};

export type SamplerPriorityPolicy = {
  enabled: boolean;
  /** Blend between Tailor's learned acquisition and declared user utility. */
  strength: number;
  axes: SamplerAxisPriority[];
  postStall?: {
    enabled: boolean;
    weight: number;
    /** Used until Tailor has observed enough lift data to infer the peak. */
    fallbackOnset: number;
    transitionWidth?: number;
  };
};

export type SamplerConfig = {
  engine: SamplerEngine;
  mannequinFamily?: MannequinFamily;
  mannequinSeed?: number;
  /** Optional adversarial features planted in the hidden Mannequin truth. */
  mannequinPathologies?: 'none' | 'sampled';
  /** Size of the non-adaptive LHS design used before Tailor scoring begins. */
  initialDesignSize?: number;
  /** Backwards-compatible single reflection prior. */
  symmetry?: SamplerSymmetryPolicy;
  /** Independent reflection priors. Multiple half-domain policies restrict
   *  the physical design to their intersection; mirrored observations expand
   *  across every declared axis. */
  symmetries?: SamplerSymmetryPolicy[];
  /** User-declared scientific utility layered onto Tailor's learned policy. */
  priority?: SamplerPriorityPolicy;
  tailorWeights?: Partial<Record<TailorExpertName, number>>;
};

export type TailorExpertName =
  | 'value_uncertainty'
  | 'gradient'
  | 'topology'
  | 'downstream'
  | 'coverage';

export type TailorExpertScore = {
  name: TailorExpertName;
  score: number;
  weight: number;
};

export type SamplerDecision = {
  engine: SamplerEngine;
  label: string;
  selectedBy: TailorExpertName | 'initial_design' | 'mock_score';
  reason: string;
  expertScores?: TailorExpertScore[];
  totalScore?: number;
  priorityScore?: number;
  priorityStrength?: number;
  prioritySummary?: string;
  /** Per-output relevance derived from the proposal-time GP's learned ARD
   *  lengthscales as span / (span + lengthscale), bounded to [0, 1]. */
  ardAxisRelevance?: Record<string, Record<string, number>>;
  mannequinInstanceId?: string;
  frontierLimit?: number;
};

export type CampaignConfig = {
  name: string;
  inputs: AxisSpec[];
  outputs: OutputSpec[];
  budget: number;
  batchSize: number;
  lowFiSolver: SolverChoice;
  highFiSolver: SolverChoice;
  computePool: string;
  flexCreditsPerHighFiRun: number;
  stopEarlyIfTargetsMet: boolean;
  postDivergenceSampling?: PostDivergenceSamplingPolicy;
  sampler?: SamplerConfig;
  oracle: OracleSpec;
};

export type SamplePoint = {
  id: string;
  iter: number;
  batchId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  source: 'sampler' | 'manual';
  inputs: Record<string, number>;
  outputs?: Record<string, number>;
  truthOutputs?: Record<string, number>;
  predicted?: Record<string, { mean: number; sigma: number }>;
  samplerDecision?: SamplerDecision;
  runtimeSec?: number;
  costFlexCredits?: number;
  flow360CaseUrl?: string;
};

export type CampaignMetrics = {
  perOutput: Record<string, {
    maxSigmaByIter: number[];
    meanSigmaByIter: number[];
    realRmseByIter?: number[];
    currentMaxSigma: number;
    currentMeanSigma: number;
    currentRealRmse?: number;
    targetSigma?: number;
    calibration?: Array<{ predictedSigma: number; realisedAbsError: number }>;
  }>;
  cumulativeCostFlexCredits: number;
  estimatedCompletionIso?: string;
  autonomousRunCount: number;
  manualRunCount: number;
};

export type CampaignState = {
  id: string;
  config: CampaignConfig;
  status: 'configuring' | 'running' | 'paused' | 'done' | 'failed';
  iteration: number;
  pointsComplete: number;
  pointsInFlight: number;
  points: SamplePoint[];
  metrics: CampaignMetrics;
  samplerRuntime?: {
    engine: SamplerEngine;
    mannequinInstanceId?: string;
    label?: string;
    backendEngine?: string;
    surrogate?: string;
    navigatorSha256?: string;
    navigatorArchitecture?: string;
    policySource?: string;
    totalObservations?: number;
    fitObservations?: number;
    approximationMode?: string;
    activeSetCap?: number;
    effectiveFitRows?: number;
    totalTransportObservations?: number;
    returnedTransportObservations?: number;
    blockSize?: number;
    fallback?: false;
  };
  createdAt: string;
  updatedAt: string;
};

export type SliceState = {
  xInput: string;
  yInput: string;
  pinned: Record<string, number>;
  slabFraction: number;
};

export type SurfaceResponse = {
  x: number[];
  y: number[];
  mean: number[][];
  sigma: number[][];
  priorMean?: number[][];
  truthMean?: number[][];
  /** Streaming transport may return a bounded display subset while the
   * posterior still represents the complete campaign history. */
  totalTransportObservations?: number;
  returnedTransportObservations?: number;
  transportMc?: {
    members: number;
    rowFraction: number;
    lengthscaleJitter: number;
    method: string;
  };
  /** Per-observation transport onto this exact posterior slice. These values
   * are returned by the same runtime/GP that produced `mean` and `sigma`. */
  transport?: Record<string, {
    mean: number;
    sigma: number;
    correlation: number;
    hiddenDistance: number;
    hiddenMaxDelta: number;
    oracleMean: number;
    /** Ensemble Monte-Carlo transport samples (fuzzy-cloud rendering). */
    mc?: number[];
    mcSigma?: number;
  }>;
};

export type ScoreResponse = {
  x: number[];
  y: number[];
  score: number[][];
  topK: Array<{ x: number; y: number; rank: number }>;
};

export type OracleAxis = { name: string; min: number; max: number; units: string };

export type OracleOutputMapping = {
  canonicalName: string;
  oracleColumnName: string;
  available: boolean;
};

export type OracleDataset = {
  id: string;
  label: string;
  axes: OracleAxis[];
  outputMappings: OracleOutputMapping[];
  pointCount: number;
  notes?: string;
};

// ---------------------------------------------------------------------------
// Shared vocabulary — canonical input/output names and defaults.
// These are the only names other workers should reference when constructing
// AxisSpec / OutputSpec objects for the demo campaigns.
// ---------------------------------------------------------------------------

export type CanonicalInputName =
  | 'alpha' | 'beta' | 'Mach' | 'Re' | 'RPM'
  | 'RPM_1' | 'RPM_2' | 'RPM_3' | 'RPM_4' | 'RPM_5' | 'RPM_6' | 'RPM_7' | 'RPM_8'
  | 'flap' | 'aileron' | 'elevator' | 'rudder' | 'thrust';

export type CanonicalOutputName = 'CL' | 'CD' | 'CY' | 'Cmx' | 'Cmy' | 'Cmz';

export const CANONICAL_OUTPUTS: readonly CanonicalOutputName[] = [
  'CL', 'CD', 'CY', 'Cmx', 'Cmy', 'Cmz',
] as const;

export const CANONICAL_INPUT_DEFAULTS: Record<
  CanonicalInputName,
  { units: string; defaultRange?: [number, number]; defaultFixed?: number }
> = {
  alpha:    { units: 'deg',           defaultRange: [-5, 20] },
  beta:     { units: 'deg',           defaultRange: [-10, 10] },
  Mach:     { units: 'dimensionless', defaultRange: [0.1, 0.8] },
  Re:       { units: 'dimensionless', defaultRange: [5e5, 5e7] },
  RPM:      { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_1:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_2:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_3:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_4:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_5:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_6:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_7:    { units: 'rpm',           defaultRange: [0, 6000] },
  RPM_8:    { units: 'rpm',           defaultRange: [0, 6000] },
  flap:     { units: 'deg',           defaultRange: [-20, 20] },
  aileron:  { units: 'deg',           defaultRange: [-20, 20] },
  elevator: { units: 'deg',           defaultRange: [-20, 20] },
  rudder:   { units: 'deg',           defaultRange: [-20, 20] },
  thrust:   { units: 'N',             defaultRange: [0, 1000] },
};

export type ControlAction = 'pause' | 'resume' | 'stop';

export type WebSocketEvent =
  | { type: 'state';     campaignId: string; state: CampaignState }
  | { type: 'point';     campaignId: string; point: SamplePoint }
  | { type: 'batch';     campaignId: string; iteration: number; pointIds: string[] }
  | { type: 'metrics';   campaignId: string; metrics: CampaignMetrics }
  | { type: 'completed'; campaignId: string }
  | { type: 'error';     campaignId: string; message: string };

export const FEATURE_FLAG_ORACLE_MODE = 'adaptive_sampling_oracle_mode' as const;
